import apiClient from "./apiClient";

const token = localStorage.getItem("accessToken");

const invalidateProductsCache = () => {
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
};

const refreshProductsCache = async () => {
  try {
    const response = await apiClient.get("/products/");
    localStorage.setItem('products', JSON.stringify(response.data));
    localStorage.setItem('productsTimestamp', Date.now().toString());
    return response.data;
  } catch (error) {
    console.error('Error refreshing products cache:', error);
    throw error;
  }
};

// Add enhanced retry logic helper
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    // Only retry on timeout or network errors
    const isTimeout = error.code === 'ECONNABORTED' || 
                      error.message?.includes('timeout') || 
                      error.isTimeout || 
                      (error.originalError && (error.originalError.code === 'ECONNABORTED' || 
                                              error.originalError.message?.includes('timeout')));
                      
    const isNetworkError = error.message === 'Network Error' || 
                           error.isNetworkError || 
                           (error.originalError && error.originalError.message === 'Network Error');
    
    // Don't retry on auth errors, validation errors, etc.
    const isRetryable = isTimeout || isNetworkError;
    
    if (retries <= 0 || !isRetryable) {
      console.warn(`Not retrying request: ${retries <= 0 ? 'max retries reached' : 'error not retryable'}`, error);
      throw error;
    }
    
    console.info(`Retrying request after ${delay}ms, ${retries} retries left...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 1.5);
  }
};

export const getProducts = async (params = {}, forceRefresh = false) => {  try {
    const token = localStorage.getItem('accessToken');
    // Try to get from cache first if not forcing refresh, no filters are active, and user is not authenticated
    const hasFilters = params.search || params.category_name || params.page > 1 || params.on_sale || params.brand;
    if (!token && !forceRefresh && !hasFilters) {
      const cached = localStorage.getItem('products');
      const timestamp = localStorage.getItem('productsTimestamp');
      if (cached && timestamp && (Date.now() - Number(timestamp)) < 300000) { // 5 min cache
        const data = JSON.parse(cached);
        if (data.results?.length > 0) return data;
      }
    }

    const queryParams = new URLSearchParams();
    
    // Handle pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size || 24);
    // Handle filter params
    if (params.search) queryParams.append('search', params.search);
    if (params.category_name) queryParams.append('category_name', params.category_name);
    if (params.brand) queryParams.append('brand', params.brand);
    if (params.is_available !== undefined) queryParams.append('is_available', params.is_available);
    if (params.stock_filter) queryParams.append('stock_filter', params.stock_filter);
    if (params.price_filter) queryParams.append('price_filter', params.price_filter);
    if (params.on_sale) queryParams.append('on_sale', 'true');
    if (params.ordering) queryParams.append('ordering', params.ordering);
    
    // Log for debugging
    console.log('API Request params:', params);
    console.log('Query string:', queryParams.toString());
    
    // Use optimized endpoint for category filtering
    let url;
    if (params.category_name && !params.on_sale) {
      url = `/products/by-category/?${queryParams.toString()}`;
      console.log('Using optimized category endpoint');
    } else {
      url = `/products/?${queryParams.toString()}`;
    }
    
    // Optimize timeout based on type of request
    const timeout = hasFilters ? 8000 : 15000; // Even shorter timeout for optimized category requests
    
    const response = await retryRequest(async () => {
      return await apiClient.get(url, { 
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(forceRefresh ? {'Cache-Control': 'no-cache'} : {})
        },
        timeout: timeout
      });
    }, hasFilters ? 2 : 3); // Fewer retries for filtered requests

    // Log API response for debugging
    console.log('API Response:', response.data);

    const formattedResponse = {
      results: response.data.results || [],
      count: response.data.count || 0,
      next: response.data.next,
      previous: response.data.previous
    };    // Cache successful response only for base requests
    if (!token && (!params.page || params.page === 1) && !params.search && !params.category_name && !params.on_sale && !params.brand) {
      localStorage.setItem('products', JSON.stringify(formattedResponse));
      localStorage.setItem('productsTimestamp', Date.now().toString());
    }

    return formattedResponse;

  } catch (error) {
    console.error('Error in getProducts:', error);
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      throw new Error('La solicitud tardó demasiado tiempo. Por favor, inténtalo nuevamente.');
    }
    throw error;
  }
}

const PRODUCT_SAVE_WITH_FILES_TIMEOUT_MS = 120000;

function formatProductSaveError(error) {
  const d = error.response?.data;
  if (!d) return error.message || 'Error al guardar el producto';
  if (typeof d === 'string') return d;
  if (d.message) return d.message;
  if (d.error) return typeof d.error === 'string' ? d.error : JSON.stringify(d.error);
  if (d.images) {
    const im = d.images;
    return Array.isArray(im) ? im.join(' ') : String(im);
  }
  if (d.payload) {
    const p = d.payload;
    return Array.isArray(p) ? p.join(' ') : String(p);
  }
  if (d.non_field_errors?.length) return d.non_field_errors.join(' ');
  return 'Error al guardar el producto';
}

// Helper para detectar si un producto está en oferta
export const isProductOnSale = (product) => {
  // Verificar si el producto tiene descuento directo
  if (product.discount && parseFloat(product.discount) > 0) {
    return true;
  }
  
  // Verificar si el precio final es menor que el precio original
  if (product.final_price && product.price && parseFloat(product.final_price) < parseFloat(product.price)) {
    return true;
  }
  
  // Verificar si hay descuentos totales (que pueden venir de ofertas activas)
  if (product.total_discount && parseFloat(product.total_discount) > 0) {
    return true;
  }
  
  // Verificar si tiene promoción especial marcada
  if (product.has_special_promotion) {
    return true;
  }
  
  return false;
};

export const getProductsOnSale = async () => {
  try {
    const response = await apiClient.get('/products/on-sale/', {
      timeout: 15000 // Smaller timeout for on-sale products
    });
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching products on sale:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

export const getProduct = async (id, forceRefresh = false) => {
  const idStr = id != null ? String(id).trim() : '';
  if (!/^\d+$/.test(idStr)) {
    const err = new Error('Identificador de producto no válido');
    err.status = 404;
    throw err;
  }
  try {
    // Use the retryRequest helper for better timeout handling
    const response = await retryRequest(async () => {
      return await apiClient.get(`/products/${idStr}/`, {
        timeout: 15000, // 15 second timeout
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(forceRefresh
            ? { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
            : { 'Cache-Control': 'max-age=300' } // 5 minute cache
          )
        },
        params: forceRefresh ? { t: Date.now() } : undefined
      });
    }, 3, 1000); // 3 retries with 1s, 1.5s, 2.25s delays
    
    if (!response.data) {
      throw new Error('No se encontró el producto');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching product:', error.response || error);
    
    // Handle timeout errors specifically
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
        (error.originalError && (error.originalError.code === 'ECONNABORTED' || 
         error.originalError.message?.includes('timeout')))) {
      throw new Error('La solicitud tardó demasiado tiempo. Por favor, inténtalo nuevamente.');
    }
    
    // Capturar específicamente errores 404 y otros errores de respuesta
    if (error.response) {
      const errorStatus = error.response.status;
      const errorData = error.response.data;
      
      // Incluir el status en el error para manejarlo en el componente
      const errorObj = new Error(
        errorData?.message || 
        errorData?.error || 
        'Error al obtener el producto'
      );
      errorObj.status = errorStatus;
      throw errorObj;
    }
    
    // Network or other errors
    throw new Error('Error de conexión al obtener el producto. Verifica tu conexión a internet.');
  }
};

/**
 * @param {{ imageFile?: File, image2File?: File }} imageFiles - Si hay archivos, el backend recibe multipart
 *   (payload JSON + image_file / image_2_file) y sube a Cloudinary en el servidor.
 */
export const updateProduct = async (productId, productData, imageFiles = {}) => {
  try {
    const dataToSend = {
      ...productData
    };

    if (productData.name !== undefined) {
      dataToSend.name = productData.name?.trim();
      dataToSend.brand = (productData.brand || '').trim();
      dataToSend.description = (productData.description || '').trim();
      dataToSend.description_marVerde = (productData.description_marVerde || '').trim();
      dataToSend.category = productData.category || '';
      dataToSend.volume = (productData.volume || '').trim();
      dataToSend.dimensions = (productData.dimensions || '').trim();
      dataToSend.image = productData.image || null;
      dataToSend.image_2 = productData.image_2 || null;
      dataToSend.features = Array.isArray(productData.features) ? productData.features : [];
      dataToSend.is_available = Boolean(productData.is_available);
      dataToSend.is_available_marVerde = Boolean(productData.is_available_marVerde);
    }

    if (productData.price !== undefined) {
      dataToSend.price = Number(productData.price);
    }
    if (productData.stock !== undefined) {
      dataToSend.stock = Number(productData.stock);
    }
    if (productData.discount !== undefined) {
      dataToSend.discount = Number(productData.discount);
    }

    const { imageFile, image2File } = imageFiles;
    const hasFiles = !!(imageFile || image2File);

    const response = hasFiles
      ? await (() => {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(dataToSend));
          if (imageFile) fd.append('image_file', imageFile);
          if (image2File) fd.append('image_2_file', image2File);
          return apiClient.patch(`/products/${productId}/`, fd, {
            timeout: PRODUCT_SAVE_WITH_FILES_TIMEOUT_MS,
          });
        })()
      : await apiClient.patch(`/products/${productId}/`, dataToSend, {
          headers: { 'Content-Type': 'application/json' },
        });

    invalidateProductsCache();
    refreshProductsCache();

    return response.data;
  } catch (error) {
    console.error('Error updating product:', error.response || error);
    throw new Error(formatProductSaveError(error));
  }
};

export const createProduct = async (productData, imageFiles = {}) => {
  try {
    console.log('Creando producto:', productData);
    const { imageFile, image2File } = imageFiles;
    const hasFiles = !!(imageFile || image2File);

    const response = hasFiles
      ? await (() => {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(productData));
          if (imageFile) fd.append('image_file', imageFile);
          if (image2File) fd.append('image_2_file', image2File);
          return apiClient.post('/products/', fd, {
            timeout: PRODUCT_SAVE_WITH_FILES_TIMEOUT_MS,
          });
        })()
      : await apiClient.post('/products/', productData, {
          headers: { 'Content-Type': 'application/json' },
        });

    invalidateProductsCache();
    await refreshProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(formatProductSaveError(error));
  }
};

export const deleteProduct = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/products/${productId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    
    // Invalidar caché al eliminar
    invalidateProductsCache();
    
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al eliminar el producto');
  }
};

export const getCategories = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get('/products/categories/', {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Error al cargar las categorías');
  }
};

// New function to create a category
export const createCategory = async (categoryData) => {
  try {
    const token = localStorage.getItem("accessToken");
    // Make sure we're using the correct endpoint
    const response = await apiClient.post('/categories/', categoryData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating category:', error);
    throw new Error(error.response?.data?.message || error.response?.data?.error || 'Error al crear la categoría');
  }
};

// New function to delete a category
export const generateProductSku = async (name, categoryName = '', variantSuffix = '') => {
  try {
    const token = localStorage.getItem('accessToken');
    const params = { name };
    if (categoryName) params.category_name = categoryName;
    if (variantSuffix) params.variant_suffix = variantSuffix;
    const response = await apiClient.get('/products/generate-sku/', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.sku;
  } catch (error) {
    console.error('Error generating SKU:', error);
    throw new Error(error.response?.data?.error || 'Error al generar SKU');
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/categories/${categoryId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw new Error(
      error.response?.data?.error || 
      error.response?.data?.message || 
      'Error al eliminar la categoría'
    );
  }
};

export const getRelatedProducts = async (productId) => {
  try {
    const response = await apiClient.get(`/products/${productId}/related/`, {
      timeout: 8000,
    });

    if (!response.data) {
      return { results: [] };
    }

    if (response.data.results) {
      return response.data;
    }
    if (Array.isArray(response.data)) {
      return {
        count: response.data.length,
        next: null,
        previous: null,
        results: response.data
      };
    }

    return { results: [] };
  } catch (error) {
    console.error('Error fetching related products:', error);

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return { results: [] };
    }

    if (error.message === 'Network Error') {
      return { results: [] };
    }

    return { results: [] };
  }
};

export const getSearchSuggestions = async (query) => {
  try {
    if (!query || query.length < 2) return { results: [] };
    
    // Enviar el query directamente - el backend ahora maneja la tokenización
    // Usar timeout corto para sugerencias rápidas
    const response = await apiClient.get(`/products/?search=${encodeURIComponent(query)}&page_size=6`, {
      timeout: 5000 // Timeout más corto para sugerencias
    });
    
    return { results: response.data?.results || [] };
  } catch (error) {
    console.error('Error fetching suggestions:', error);
    return { results: [] };
  }
};

// === PRODUCT REVIEWS ===

// Obtener reseñas para un producto específico
export const getProductReviews = async (productId) => {
  try {
    const response = await apiClient.get(`/reviews/for_product/?product_id=${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product reviews:', error);
    return [];
  }
};

// Crear una nueva reseña
export const createProductReview = async (reviewData) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.post("/reviews/", reviewData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating review:', error);
    throw new Error(
      error.response?.data?.error || 
      error.response?.data?.message || 
      'Error al crear la reseña'
    );
  }
};

// Verificar si el usuario ya ha dejado una reseña para un producto
export const hasUserReviewed = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return { exists: false };
    
    const response = await apiClient.get(`/reviews/has_reviewed/?product_id=${productId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking user review:', error);
    return { exists: false };
  }
};

// Obtener reseñas del usuario actual
export const getUserReviews = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get("/reviews/my_reviews/", {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user reviews:', error);
    throw new Error('Error al obtener las reseñas del usuario');
  }
};

// === ADMIN FUNCTIONS ===

// Obtener reseñas pendientes de aprobación
export const getPendingReviews = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get("/reviews/pending/", {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pending reviews:', error);
    throw new Error('Error al obtener las reseñas pendientes');
  }
};

// Aprobar una reseña
export const approveReview = async (reviewId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.patch(`/reviews/${reviewId}/approve/`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error approving review:', error);
    throw new Error('Error al aprobar la reseña');
  }
};

// Rechazar una reseña
export const rejectReview = async (reviewId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.patch(`/reviews/${reviewId}/reject/`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error rejecting review:', error);
    throw new Error('Error al rechazar la reseña');
  }
};

// Eliminar una reseña
export const deleteReview = async (reviewId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/reviews/${reviewId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting review:', error);
    throw new Error('Error al eliminar la reseña');
  }
};
// Alias para deleteReview (compatibilidad con componentes que usan este nombre)
export const deleteProductReview = deleteReview;

// Obtener todas las reseñas (admin)
export const getAllReviews = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.get("/reviews/", {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all reviews:', error);
    throw new Error('Error al obtener todas las reseñas');
  }
};